/**
 * @typedef {object} CallModalParams
 * @prop {number} meetingId TY meeting ID
 * @prop {string|"incoming"|"outgoing"} callType Is it an outgoing or incoming call
 * @prop {string} userName Name of the user to display
 * @prop {string} userImage Image of the user to display
 */

(function () {
    angular.module('tradersyard').factory('CallService', callServiceFactory);

    // region Event Types

    const EVENT_CALL_REQUEST = 'meeting:call:request';
    const EVENT_CALL_START = 'meeting:call:start';

    /**
     * Called once you join the call room
     * @type {string}
     */
    const EVENT_CALL_CONNECT = 'meeting:call:connect';
    const EVENT_CALL_ACCEPT = 'meeting:call:participant:accept';
    const EVENT_CALL_REJECT = 'meeting:call:participant:reject';
    const EVENT_CALL_REPLIED = 'meeting:call:replied';
    const EVENT_CALL_TIMEOUT = 'meeting:call:timeout';
    const EVENT_CALL_END = 'meeting:call:end';
    const EVENT_CALL_RECONNECT = 'meeting:call:participant:connect';

    const CALL_INCOMING = 'incoming';
    const CALL_OUTGOING = 'outgoing';

    const WINDOW_OPTIONS =
        'width=1260,height=720,toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=yes';

    // endregion

    callServiceFactory.$inject = [
        '$rootScope',
        '$window',
        'WebSocket',
        'graph-api-client',
        '$q',
        '$uibModal',
        '$location',
    ];

    /**
     *
     * @param $rootScope
     * @param $window
     * @param WebSocket
     * @param apiClient
     * @param $q
     * @param $uibModal
     * @param {angular.ILocationService} $location
     * @return {{onceMeetingCallReplied: (function(number): angular.IPromise), onceMeetingCallEnd: (function(number): angular.IPromise), onCallAccepted: onCallAccepted, rejectCall: (function(*): *), onceMeetingCallReconnect: (function(number): angular.IPromise), onIncomingCall: onIncomingCall, onceMeetingCallConnect: (function(number): angular.IPromise), CALL_INCOMING: string, startCall: (function(*=): angular.IPromise<Window>), onceMeetingCallRejected: (function(number): angular.IPromise), createCall: (function(*=): *), onceMeetingCallAccepted: (function(*=): angular.IPromise), acceptCall: (function(*): *), onCallRejected: onCallRejected, openCallAppWindow: (function({meetingId: number, callType: ("incoming"|"outgoing")}): *), onceMeetingCallTimeout: (function(number): angular.IPromise), CALL_OUTGOING: string}}
     */
    function callServiceFactory($rootScope, $window, WebSocket, apiClient, $q, $uibModal, $location) {
        var onceMeetingCallRejected = onceMeetingCallEventFactory(EVENT_CALL_REJECT);
        var onceMeetingCallTimeout = onceMeetingCallEventFactory(EVENT_CALL_TIMEOUT);
        var onceMeetingCallReplied = onceMeetingCallEventFactory(EVENT_CALL_REPLIED);
        var onceMeetingCallEnd = onceMeetingCallEventFactory(EVENT_CALL_END);
        var onceMeetingCallConnect = onceMeetingCallEventFactory(EVENT_CALL_CONNECT);
        var onceMeetingCallReconnect = onceMeetingCallEventFactory(EVENT_CALL_RECONNECT);

        return {
            startCall: startCall,
            createCall: createCall,
            acceptCall: acceptCall,
            rejectCall: rejectCall,
            openCallAppWindow: openCallAppWindow,
            // promise bases call event handling
            onceMeetingCallAccepted: onceMeetingCallAccepted,
            onceMeetingCallRejected: onceMeetingCallRejected,
            onceMeetingCallReplied: onceMeetingCallReplied,
            onceMeetingCallTimeout: onceMeetingCallTimeout,
            onceMeetingCallEnd: onceMeetingCallEnd,
            onceMeetingCallConnect: onceMeetingCallConnect,
            onceMeetingCallReconnect: onceMeetingCallReconnect,
            // regular event handling
            onCallAccepted: onCallAccepted,
            onCallRejected: onCallRejected,
            onIncomingCall: onIncomingCall,
            // flags
            CALL_INCOMING: CALL_INCOMING,
            CALL_OUTGOING: CALL_OUTGOING,
        };

        /**
         * Starts a call procedure for a meeting. It is only useful for **private** meetings.
         * Opens an outgoing call window, which is a {@link tyCallApp} popup window.
         * Rest of the process is handled by the Call App.
         *
         * @param meetingId
         * @returns {ng.IPromise<Window>}
         */
        function startCall(meetingId) {
            return openOutgoingCallPopup({
                meetingId: meetingId,
                callType: CALL_OUTGOING,
            });
        }

        /**
         * Creates a call request on the server. Nothing more.
         * If you want to start a call procedure see {@link startCall}
         * @param meetingId
         * @returns {angular.IPromise<any> | PromiseLike<any> | Promise<any> | *}
         */
        function createCall(meetingId) {
            return apiClient.post(['meeting', meetingId, 'call']).then(function () {
                // wait for other person to "answer the phone".
                return callRequestAnswerPromise(meetingId);
            });
        }

        function onIncomingCall(callback) {
            // IMPORTANT: handle case when user is already in a call or someone else is calling right now.
            const qs = $location.search();

            // if (qs.webview) {
            //     // TODO: Hotfix: do nothing if we are running inside a webview - this is based on detecting a "webview" QS in URL.
            //     console.log('Will not accept incoming calls. Running in webview.');
            //     return;
            // }

            WebSocket.on(EVENT_CALL_REQUEST, function (callRequestEvent) {
                console.log('Call request detected.');

                if (Number(callRequestEvent.user.id) === Number($rootScope.me.id)) {
                    console.log('We started this call. Ignoring call request.');
                    return; // it's our call request
                }

                console.log('Proceeding with call request.');

                console.log('call request', callRequestEvent);

                var callModalParams = {
                    meetingId: callRequestEvent.place.id,
                    callType: CALL_INCOMING,
                    userName: callRequestEvent.user.name,
                    userImage: callRequestEvent.user.picture.source,
                };

                var answerPromise = openIncomingCallPopup(callModalParams);

                callback(answerPromise);
            });
        }

        // region Event Hooks

        function onCallAccepted(callback) {
            WebSocket.on(EVENT_CALL_ACCEPT, function (acceptEvent) {
                callback(acceptEvent);
            });
        }

        function onCallRejected(callback) {
            WebSocket.on(EVENT_CALL_REJECT, function (rejectEvent) {
                callback(rejectEvent);
            });
        }

        /**
         * Resolves a promise when call is answered.
         * It needs to verify both meeting ID and user ID to avoid ambiguity.
         *
         * @param meetingId
         * @returns {ng.IPromise}
         */
        function onceMeetingCallAccepted(meetingId) {
            var verifyMeetingAndUserPredicate = _verifyEventMeetingAndUser(meetingId);
            return WebSocket.onEventPromise(EVENT_CALL_ACCEPT, verifyMeetingAndUserPredicate);
        }

        /**
         * Creates a function that will answer to call event only once.
         * Verifies meeting ID.
         *
         * @param callEvent
         * @returns {function(number): ng.IPromise}
         */
        function onceMeetingCallEventFactory(callEvent) {
            return function (meetingId) {
                var verifyMeetingPredicate = _verifyEventMeetingId(meetingId);
                return WebSocket.onEventPromise(callEvent, verifyMeetingPredicate);
            };
        }

        // endregion

        function acceptCall(meetingId) {
            return apiClient.httpPut(['meeting', meetingId, 'call']);
        }

        function rejectCall(meetingId) {
            return apiClient.delete(['meeting', meetingId, 'call']);
        }

        /**
         * Open a call modal for an outgoing call (when you call someone).
         * It basically starts the `tyCallApp` component.
         *
         * @param {object} callParams
         * @returns {ng.IPromise<Window>} Resolves with an opened Window instance
         */
        function openOutgoingCallPopup(callParams) {
            return openCallAppWindow(callParams);
        }

        /**
         * Will inform you about the status of call request as a {@link ng.IPromise}.
         * Resolves when call is accepted. Rejects when call is rejected or timesout.
         *
         * @param meetingId
         * @returns {ng.IPromise}
         */
        function callRequestAnswerPromise(meetingId) {
            // REFACTOR: Accept multiple events for a single promise,
            //  so we can unsubscribe all listeners when promise resolves.
            //  Right now the unresolved promises are "orphaned".
            var acceptsCall = onceMeetingCallAccepted(meetingId);

            /**
             * IMPORTANT: Sometimes the "reject" event is not generated,
             *  if same user starts a call from 2 different clients at the same time.
             */
            var endsCall = onceMeetingCallEnd(meetingId).then(function () {
                return $q.reject(EVENT_CALL_REJECT);
            });

            var callTimeout = onceMeetingCallTimeout(meetingId).then(function () {
                return $q.reject(EVENT_CALL_TIMEOUT);
            });

            return $q.race([acceptsCall, endsCall, callTimeout]);
        }

        /**
         * Open a call modal for an incoming call (when someone calls you).
         * When call is accepted, the tyCallApp is opened in a new window.
         *
         * @param {CallModalParams} callModalParams
         * @returns {ng.IPromise}
         */
        function openIncomingCallPopup(callModalParams) {
            var callModal = $uibModal.open({
                component: 'tyIncomingCallModal',
                backdrop: 'static',
                resolve: {
                    params: function () {
                        return callModalParams;
                    },
                },
            });

            return callModal.result;
        }

        function _verifyEventMeetingId(meetingId) {
            return function (eventData) {
                return Number(eventData.place.id) === Number(meetingId);
            };
        }

        function _verifyEventUserId(eventData) {
            return Number(eventData.user.id) !== Number($rootScope.me.id);
        }

        function _verifyEventMeetingAndUser(meetingId) {
            var verifyMeeting = _verifyEventMeetingId(meetingId);

            return function (eventData) {
                return verifyMeeting(eventData) && _verifyEventUserId(eventData);
            };
        }

        /**
         * @param {{meetingId: number, callType: "incoming"|"outgoing"}} callParams
         * @returns {angular.IPromise<Window>}
         */
        function openCallAppWindow(callParams) {
            // The window should have a limited UI.
            const url = _getVideoCallUrl(callParams.meetingId, callParams.callType);
            const popupWindow = $window.open(url, 'targetWindow', WINDOW_OPTIONS);
            return $q.resolve(popupWindow);
        }

        function _getVideoCallUrl(meetingId, callType) {
            const queryParams = [`meeting_id=${meetingId}`, `call_type=${callType}`].join('&');
            return $window.location.origin + '/video-call/' + meetingId + '?' + queryParams;
        }
    }
})();
